import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    FormHelperText,
    CircularProgress,
    useTheme,
} from '@mui/material';
import automationService from '../services/automation.service';
import { buildRulePayload, parseRuleToFormState } from '../config/automationConfig';
import { usePreferences } from '../contexts/PreferencesContext';

const getTextFieldStyle = (theme) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
    },
});

const AutomationRuleSettings = ({ rule, onSaved }) => {
    const theme = useTheme();
    const { t } = usePreferences();
    const [duration, setDuration] = useState(30);
    const [notificationTitle, setNotificationTitle] = useState('');
    const [notificationMessage, setNotificationMessage] = useState('');
    const [saving, setSaving] = useState(false);

    const triggerType = rule?.trigger_type;
    const isBreak = triggerType === 'long_break';
    const needsTitle = triggerType === 'long_shift_reminder' || triggerType === 'auto_end_shift';

    useEffect(() => {
        if (!rule) return;
        const form = parseRuleToFormState(rule);
        setDuration(form.duration);
        setNotificationTitle(form.notificationTitle);
        setNotificationMessage(form.notificationMessage);
    }, [rule]);

    const helperText = () => {
        if (triggerType === 'long_break') return t('pages.automations.fields.helperBreak');
        if (triggerType === 'long_shift_reminder') return t('pages.automations.fields.helperShiftReminder');
        return t('pages.automations.fields.helperAutoEnd');
    };

    const handleSave = async () => {
        if (!rule?.id) return;
        setSaving(true);
        try {
            const payload = buildRulePayload(rule, {
                duration,
                notificationTitle,
                notificationMessage,
            });
            await automationService.updateRule(rule.id, payload);
            onSaved?.(payload);
        } catch (error) {
            console.error('Failed to save automation settings', error);
        } finally {
            setSaving(false);
        }
    };

    if (!rule) {
        return null;
    }

    return (
        <Box sx={{ pt: 1 }}>
            <TextField
                fullWidth
                size="small"
                margin="dense"
                type="number"
                label={isBreak ? t('pages.automations.fields.durationMinutes') : t('pages.automations.fields.durationHours')}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                helperText={helperText()}
                sx={getTextFieldStyle(theme)}
            />

            {needsTitle && (
                <TextField
                    fullWidth
                    size="small"
                    margin="dense"
                    label={t('pages.automations.fields.notificationTitle')}
                    value={notificationTitle}
                    onChange={(e) => setNotificationTitle(e.target.value)}
                    sx={getTextFieldStyle(theme)}
                />
            )}

            <TextField
                fullWidth
                size="small"
                margin="dense"
                label={t('pages.automations.fields.notificationMessage')}
                value={notificationMessage}
                onChange={(e) => setNotificationMessage(e.target.value)}
                multiline
                minRows={3}
                sx={getTextFieldStyle(theme)}
            />

            <FormHelperText sx={{ ml: 0.5, mb: 1 }}>
                {needsTitle
                    ? t('pages.automations.fields.placeholdersHours')
                    : t('pages.automations.fields.placeholdersEmployee')}
            </FormHelperText>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || !duration || (needsTitle && !notificationTitle.trim()) || !notificationMessage.trim()}
                    sx={{ borderRadius: '12px', boxShadow: 'none' }}
                >
                    {saving ? <CircularProgress size={20} color="inherit" /> : t('pages.automations.save')}
                </Button>
            </Box>
        </Box>
    );
};

export default AutomationRuleSettings;
